import type { ReactNode } from 'react';
import { Body1, Subtitle2, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  type FluentIcon,
  CheckmarkCircle48Regular,
  DocumentAdd48Regular,
  ErrorCircle48Regular,
  LockClosed48Regular,
  QuestionCircle48Regular,
  Search48Regular,
} from '@fluentui/react-icons';
import { LAYOUT, SPACE } from '../styles/scale';
import { useSurfaceStyles } from '../styles/surfaces';
import { useResponsiveStyles } from '../hooks/useResponsiveStyles';

/**
 * The "nothing here" block, in its six distinct meanings (#202, wireframe 10.2).
 *
 * ── Why a variant rather than one component with different copy ──
 * Sheet 10.2 is explicit that an empty screen means several unrelated things and
 * each needs its own way out. Rendering them identically is how "your filter
 * matched nothing" becomes indistinguishable from "you have no data" — the user
 * clears an account they never filled, or files a duplicate request because the
 * one they were searching for looked absent. The app already shipped three
 * different treatments of this (`Dashboard`'s framed first-run block, its
 * inline "no matches" line, `ReviewerQueue`'s "Nothing waiting for review"),
 * which is what this replaces.
 *
 * | variant         | means                                  | the way out              |
 * | --------------- | -------------------------------------- | ------------------------ |
 * | `first-run`     | no data has ever existed here          | the PRIMARY action       |
 * | `no-results`    | data exists; this query matched none   | "Clear filters" — NEVER "New request": the user was looking for something, not creating one |
 * | `all-clear`     | a queue with nothing waiting — success | none. There is nothing to do, and offering something implies otherwise |
 * | `error`         | the fetch failed; emptiness is unknown | "Try again"              |
 * | `no-permission` | data exists and is not yours to see    | none, or a way to ask    |
 * | `not-found`     | this particular thing is gone          | a way back               |
 *
 * An error must NEVER be rendered as a plain empty state: "no requests" and
 * "we could not load your requests" are different facts, and showing the first
 * when the second is true is a lie the user acts on.
 *
 * ── Why the glyph differs per variant and not just the colour ──
 * `teamsHighContrastTheme` discards the brand hue entirely (every `colorBrand*`
 * maps to a Windows system colour), so a variant distinguished by colour is one
 * variant in high contrast. The icon SHAPE and the copy are what survive; the
 * icon itself is `aria-hidden` because the title already carries the meaning
 * and announcing both just repeats it.
 */

export type EmptyStateVariant =
  'first-run' | 'no-results' | 'all-clear' | 'error' | 'no-permission' | 'not-found';

export interface EmptyStateProps {
  variant: EmptyStateVariant;
  title: string;
  /** One sentence of what happens next, or what was searched for. */
  body?: ReactNode;
  /**
   * The single way out. `all-clear` takes none — see the table above; the
   * queue being empty is the desired state, not a problem to resolve.
   */
  action?: ReactNode;
}

/**
 * Icon per variant. Every name here was checked against the installed
 * `@fluentui/react-icons` typings before use — the 48px set is much smaller
 * than the 24px one (`DocumentSearch48Regular` and `Inbox48Regular`, the
 * obvious first picks, do not exist at this size), and a missing export is an
 * `undefined` component that renders as nothing rather than an error.
 */
const VARIANT_ICONS: Record<EmptyStateVariant, FluentIcon> = {
  'first-run': DocumentAdd48Regular,
  'no-results': Search48Regular,
  'all-clear': CheckmarkCircle48Regular,
  error: ErrorCircle48Regular,
  'no-permission': LockClosed48Regular,
  'not-found': QuestionCircle48Regular,
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: SPACE.l,
    // Overrides the card's own 16px: an empty state is mostly air by design,
    // and `xxxl` exists in the scale for exactly this one job.
    padding: SPACE.xxxl,
  },
  /**
   * De-emphasised via the neutral foreground ramp, never `opacity` — a dimmed
   * layer takes the text down with it and is invisible in high contrast, where
   * `colorNeutralForeground3` still resolves to a real system colour.
   */
  icon: { color: tokens.colorNeutralForeground3 },
  /**
   * Measured, not fluid. The frame fills the canvas so it reads as the page's
   * content rather than a note pinned to the left of it, but a sentence set
   * across 1800px is unreadable — so the copy caps at ~75ch.
   */
  copy: { maxWidth: LAYOUT.measure, color: tokens.colorNeutralForeground2 },
  title: { maxWidth: LAYOUT.measure },
});

export function EmptyState({ variant, title, body, action }: EmptyStateProps) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();
  const responsive = useResponsiveStyles();
  const Icon = VARIANT_ICONS[variant];

  return (
    // `data-variant` is the one hook a page (or a test) has for "which of the
    // six is on screen" — the copy is the caller's, so nothing else in the
    // markup identifies it.
    <div
      data-variant={variant}
      className={mergeClasses(surfaces.card, styles.root, responsive.touchTargets)}
    >
      {/* No `aria-hidden` here on purpose, and no `title`: read from
          `useBaseIconState`, a Fluent icon sets `aria-hidden` ITSELF unless it
          is given an `aria-label`/`aria-labelledby`, and it OVERRIDES whatever
          the caller passed (an explicit `aria-hidden={false}` changes nothing).
          Giving it a `title` is the one way to undo that — it flips the svg to
          `role="img"` — so the test asserts the rendered attribute rather than
          this line. The glyph is decorative; the title carries the meaning. */}
      <Icon className={styles.icon} />
      <Subtitle2 as="h2" block className={styles.title}>
        {title}
      </Subtitle2>
      {body !== undefined && (
        <Body1 block className={styles.copy}>
          {body}
        </Body1>
      )}
      {action}
    </div>
  );
}
